import $ from 'jquery';
import { useEffect, useMemo, useRef, useState } from 'react';
import 'datatables.net';
import 'datatables.net-responsive';
import { format } from 'date-fns';
import { Chip, MenuItem, OutlinedInput, Select } from '@mui/material';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';

import Loader from '@/components/ui/Loader/Loader';
import { getStatus } from '@/helper/getStatus';
import project from '@/api/project';
import { getColorStatus } from '../../../helper/getColorStatus';

const f = (d) => {
	if (!d) return;

	return (
		'<div className="col-md-12 row ellipsis-text">' + '<span><strong>Informasjon:&nbsp;</strong></span>' + '<span>' + d.note ||
		'-' + '</span>' + '</div>'
	);
};

const { getProducts } = project();

const getText = (projectsT) => {
	let txtLabel = '';
	const total = projectsT?.length || 0;
	if (total === 0) {
		txtLabel = <span className='font-bold'>Ingen dokumenter funnet</span>;
	} else if (total === 1) {
		txtLabel = (
			<>
				<span className='font-bold'>Totalt 1 </span>
				<span className='text-bd-gLight'>dokument</span>
			</>
		);
	} else if (total > 1) {
		txtLabel = (
			<>
				<span className='font-bold'>Totalt {total} </span>
				<span className='text-bd-gLight'>dokumenter</span>
			</>
		);
	}
	return txtLabel;
};

const options = [
	{ id: 'standard', name: 'Standard', value: 0 },
	{ id: 'non-standard', name: 'Non-standard', value: 1 },
	{ id: 'arkiv', name: 'Arkiv', value: 2 },
];

export default function JDataTable({ group, category, id, search, showsAll }) {
	const tableRef = useRef();
	const selectKodeRef = useRef(null);
	const projectStatus = useSelector((state) => state.content.projectStatusD);
	const pStatus = useSelector((state) => state.content.projectStatusD);
	const productPrefix = useSelector((state) => state.content.productPrefix);

	const [filter, setFilter] = useState({
		productprefixid: '',
		statuses: [],
		type: [],
	});

	const { data: d, isFetching } = useQuery({
		queryKey: ['documents-table', group, category, id, filter, search],
		queryFn: () => {
			const obj = { max_size: true };
			if (search) {
				obj.search = search;
			}
			if (filter.productprefixid) {
				obj.productprefixid = filter.productprefixid;
			}
			if (filter.statuses.length > 0) {
				obj.status = filter.statuses.map((p) => p);
			}
			if (filter.type.length > 0) {
				obj.product_type = filter.type.map((p) => p);
			}
			return getProducts(obj);
		},
		placeholderData: keepPreviousData,
		refetchOnWindowFocus: false,
	});

	const projectsT = useMemo(() => d?.data, [d]) || null;
	useEffect(() => {
		const table = $(tableRef.current).DataTable({
			data: projectsT,
			columns: [
				{ title: 'Kode', data: 'productprefix' },
				{ title: 'Nummer', data: 'productcodenumber' },
				{ title: 'Name', data: 'productname' },
				{ title: 'Version', data: 'productversion' },
				{ title: 'Bruker', data: 'createdby_email' },
				{ title: 'Dato', data: 'updatetimestamp' },
				{ title: 'Status', data: 'status' },
				{ title: 'Type', data: 'producttype' },
				{ title: 'Informasjon', data: 'description', visible: true },
				// { data: 'refs', visible: false },
				// { data: 'reference', visible: false },
				{ data: 'producttype', visible: false },
				{ data: 'status', visible: false },
			],
			destroy: true,
			// stateSave: true,
			// responsive: true,
			responsive: {
				details: {
					type: 'column',
					target: 'tr',
				},
			},
			columnDefs: [
				{
					width: '10%',
					targets: 0,
					searchable: true,
					orderable: true,
					responsivePriority: 1,
					className: 'dt-control dt-head-center relative',
				},
				{ targets: 1, responsivePriority: 2 },
				{ targets: 2, responsivePriority: 3 },
				{ targets: 3, responsivePriority: 4, width: '64px' },
				{ targets: 4, responsivePriority: 8 },
				{ targets: [1, 3, 4], className: 'document-detail dt-head-center dt-body-center' },
				{
					targets: 5,
					width: '5%',
					render: function (row) {
						let date = format(new Date(row), 'yyyy/MM/dd');
						return date;
					},
					responsivePriority: 6,
				},
				{
					targets: 6,
					searchable: false,
					orderable: true,
					type: 'alt-string',
					responsivePriority: 5,
					width: 32,
					render: function (row) {
						let stat = row ? +row : null;
						const status = getStatus(pStatus, stat);
						return status.url && status.url !== 'No icon'
							? '<div class="flex m-auto h-8"><img class="m-auto" title="' +
									status.title +
									'" alt="' +
									status.title +
									'" src="' +
									status.url +
									'"></div>'
							: '<div class="flex h-8">' + status.title + '</div>';
					},
				},
				{
					targets: 7,
					width: 32,
					searchable: false,
					orderable: true,
					type: 'alt-string',
					className: '',
					responsivePriority: 7,
					render: function (_, __, row) {
						const color = getColorStatus(row.producttype);
						return '<div class="flex m-auto h-4 w-4 rounded-full" style="background:' + color.hex + '" title="' + color.name + '"></div>';
					},
				},
				{
					targets: 8,
					width: '0',
					searchable: true,
					orderable: true,
					render: function (data) {
						const html = `<span class="text-wrap">${data}</span>`;
						return html;
					},
				},
				{
					targets: 9,
					width: '0',
					searchable: true,
					orderable: true,
				},
				{
					targets: 10,
					width: '0',
					searchable: true,
					orderable: true,
					render: function (row) {
						const stat = row;
						const pref = '12';
						let htmlStat;
						if (stat === 9) {
							htmlStat = '<span data-title="Ny" >Ny / Under Oppdatering</span>';
						} else if (stat === 2 && pref == '12') {
							//Project
							htmlStat = '<span data-title="Venter igangsettelse" >Venter kontroll</span>';
						} else if (stat === 2 && pref != '12') {
							//Component
							htmlStat = '<span data-title="Venter kontroll" >Venter kontroll</span>';
						} else if (stat === 3 && pref == '12') {
							//Project
							htmlStat = '<span data-title="Igangsatt" >Venter Godkjenning</span>';
						} else if (stat === 3 && pref != '12') {
							//Component
							htmlStat = '<span data-title="Venter Godkjenning" >Venter Godkjenning</span>';
						} else if (stat === 4 && pref != '12') {
							//Project
							htmlStat = '<span data-title="Godkjent" >Godkjent</span>';
						} else if (stat === 4 && pref == '12') {
							//Component
							htmlStat = '<span data-title="Avsluttet" >Godkjent</span>';
						} else if (stat === 5) {
							htmlStat = '<span data-title="Avslått" >Avslått</span>';
						} else if (stat === 1) {
							htmlStat = '<span data-title="Status mangler" >Status mangler</span>';
						} else {
							htmlStat = '<span data-title="Status mangler" >Status mangler</span>';
						}

						return htmlStat;
					},
				},
				{
					targets: [1, 2, 3, 4, 5],
					searchable: true,
					orderable: true,
					className: 'sorting document-detail',
				},
			],
			order: [[1, 'desc']],
			language: {
				emptyTable: 'Ingen resultater',
				info: 'Viser _START_ to _END_ of _TOTAL_ rader',
				infoEmpty: 'Viser 0 to 0 of 0 rader',
				thousands: '',
				infoFiltered: '(filtered from _MAX_ total rader)',
				lengthMenu: 'Viser _MENU_ rader',
				zeroRecords: 'Ingen resultater',
				paginate: {
					first: 'First',
					last: 'Last',
					next: 'Neste',
					previous: 'Forrige',
				},
			},
			// initComplete: function (f) {
			// 	console.log('data', f)
			// },
		});
		table.on('click', 'td.dt-control', function (e) {
			let tr = e.target.closest('tr');
			let row = table.row(tr);

			if (!row.child.isShown()) {
				row.child.hide();
			} else {
				row.child(f(row.data())).show();
			}
		});
		table.on('click', 'td.document-detail', function (e) {
			let tr = e.target.closest('tr');
			let row = table.row(tr)?.data();
			if (row) {
				const { productcodenumber: code, productversion: ver, productid: id } = row;
				window.open(window.location.origin + '/project/' + code + (ver && ver != '-' ? '_' + ver : '') + `/${id}`, '_blank');
			}
		});

		return function () {
			table.destroy();
		};
	}, [showsAll, projectsT, search, filter, pStatus]);

	const handleChangeFilter = (e, key) => {
		const val = e;
		setFilter((prevState) => {
			return { ...prevState, [key]: val };
		});
	};

	return (
		(search || showsAll) && (
			<div>
				<div>{isFetching && <Loader />}</div>
				<div className='flex flex-col gap-4'>
					<div className='flex items-center justify-between'>
						<div>{getText(projectsT)}</div>
						<div className='flex items-center gap-2'>
							<div className='flex items-center gap-1'>
								<span>Kode</span>

								<Select
									ref={selectKodeRef}
									displayEmpty
									labelId='type'
									id='type'
									className='min-w-48 select-default '
									value={filter.productprefixid}
									onChange={(e) => {
										const val = e.target.value;
										handleChangeFilter(val, 'productprefixid');
										selectKodeRef.current.blur();
									}}
									input={<OutlinedInput label='' size='small' placeholder='--Velg type--' />}
									placeholder='--Velg type--'
									MenuProps={{
										disableAutoFocus: true,
									}}
								>
									<MenuItem value=''>
										<em className='text-gray-300' value=''>
											--Velg kode--
										</em>
									</MenuItem>
									{productPrefix?.map((p) => (
										<MenuItem key={p.productprefixid} value={p.productprefixid} name={p.productprefix}>
											{p.productprefix}
										</MenuItem>
									))}
								</Select>
							</div>
							<div className='flex items-center gap-1'>
								<span>Status</span>

								<Select
									displayEmpty
									labelId='status'
									id='status'
									className='py-0 min-w-48 select-default'
									value={filter.statuses}
									onChange={(e) => {
										const val = e.target.value;
										handleChangeFilter(val.length && val.some((v) => v === '') ? [] : val, 'statuses');
										e.target.blur();
									}}
									input={<OutlinedInput label='' size='small' placeholder='--Velg status--' className='py-0' />}
									placeholder='--Velg status--'
									multiple
									renderValue={(selected) =>
										selected.map((val) => {
											const option = projectStatus.find((opt) => opt.id_status === val);
											if (!option) return '';
											return <Chip key={option.id} label={option.label} className='me-1' />;
										})
									}
									MenuProps={{
										disableAutoFocus: true,
									}}
								>
									<MenuItem value=''>
										<em className='text-gray-300'>--Velg filter--</em>
									</MenuItem>
									{projectStatus?.map((p) => (
										<MenuItem key={p.id} value={p.id_status} name={p.label}>
											{p.label}
										</MenuItem>
									))}
								</Select>
							</div>
							<div className='flex items-center gap-1'>
								<span>Type</span>

								<Select
									displayEmpty
									labelId='type'
									id='type'
									className='min-w-48 select-default '
									value={filter.type}
									onChange={(e) => {
										const val = e.target.value;
										handleChangeFilter(val.length && val.some((v) => v === '') ? [] : val, 'type');
										e.target.blur();
									}}
									input={<OutlinedInput label='' size='small' placeholder='--Velg type--' />}
									placeholder='--Velg type--'
									multiple
									renderValue={(selected) =>
										selected.map((val) => {
											const option = options.find((opt) => opt.value === val);
											if (!option) return '';
											return <Chip key={option.id} label={option.name} className='me-1' />;
										})
									}
									MenuProps={{
										disableAutoFocus: true,
									}}
								>
									<MenuItem value=''>
										<em className='text-gray-300' value=''>
											--Velg status--
										</em>
									</MenuItem>
									{options?.map((p) => (
										<MenuItem key={p.id} value={p.value} name={p.name}>
											{p.name}
										</MenuItem>
									))}
								</Select>
							</div>
						</div>
					</div>
				</div>
				<div id='dt-wrapper' className='relative w-full'>
					<table className='w-full tb-default display nowrap' ref={tableRef}>
						<thead className='thead-light'></thead>
						<tbody></tbody>
					</table>
				</div>
			</div>
		)
	);
}
